import React, { useContext, useMemo, useState } from 'react';

import { Box } from '@material-ui/core';

import { AppButton } from '@vyce/core/src/components/AppButton';
import { ConfirmDialog } from '@vyce/core/src/components/ConfirmDialog';
import { generateErrorObject, NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import {
  RolesEditorSaveOutcome,
  useRolesSaveBarEntries,
} from '@vyce/core/src/contexts/rolesSaveBarProvider';
import { AppNotification } from '@vyce/core/src/types';

import { useRolesStyles } from '../styles';
import { getSaveErrorNotification } from '../utils/permissionErrors';

// notistack defaults to bottom-left, which is exactly where this bar sits.
const SNACKBAR_ANCHOR = { vertical: 'top', horizontal: 'right' } as const;

interface Props {
  /** What the rows are, for the copy: "3 admins" / "2 companies". */
  itemLabel?: string;
  itemLabelPlural?: string;
}

// "Dave Test", "Dave Test and Matt Rantell", "Dave, Matt and Sarah" — past three
// names the list stops being readable, so the caller falls back to a count.
const formatNames = (names: string[]) => {
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * One Save for the whole page. Every open permission editor registers itself, and
 * this bar saves each one that has unsaved changes — pinned to the bottom of the
 * viewport for as long as anything is dirty.
 */
export const RolesPageSaveBar: React.FC<Props> = ({ itemLabel = 'admin', itemLabelPlural = 'admins' }) => {
  const classes = useRolesStyles();
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const entries = useRolesSaveBarEntries();
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const dirty = useMemo(
    () => entries.filter(entry => entry.summary.isDirty && entry.summary.canEdit),
    [entries]
  );
  const blocked = dirty.filter(entry => !!entry.summary.invalidReason);
  const names = dirty.map(entry => entry.summary.label).filter(Boolean);

  const summaryText =
    names.length && names.length <= 3
      ? `Unsaved changes for ${formatNames(names)}`
      : `Unsaved changes for ${dirty.length} ${dirty.length === 1 ? itemLabel : itemLabelPlural}`;

  const report = (results: { label: string; outcome: RolesEditorSaveOutcome }[]) => {
    const saved = results.filter(result => result.outcome.status === 'saved');
    const failed = results.filter(result => result.outcome.status === 'failed');

    if (!failed.length) {
      if (!saved.length) return;
      showNotification({
        message:
          saved.length === 1
            ? 'Permissions have been updated'
            : `Permissions updated for ${saved.length} ${itemLabelPlural}`,
        options: { variant: 'success', anchorOrigin: SNACKBAR_ANCHOR },
      });
      return;
    }

    // One reason per failure, so a 409 on one row doesn't hide a 500 on another.
    const reasons = failed.map(result => {
      const error = (result.outcome as { error: any }).error;
      const notification: AppNotification =
        getSaveErrorNotification(error) || generateErrorObject(error);
      return `${result.label || 'This row'}: ${notification.description || notification.message}`;
    });

    // A single failure keeps the wording it would have had without the bar, so an
    // unknown server error still reaches handleServerError's rendering.
    if (failed.length === 1 && !saved.length) {
      const error = (failed[0].outcome as { error: any }).error;
      const notification = getSaveErrorNotification(error);
      if (notification) {
        showNotification({ ...notification, options: { ...notification.options, anchorOrigin: SNACKBAR_ANCHOR } });
      } else {
        handleServerError(error);
      }
      return;
    }

    showNotification({
      message: saved.length
        ? `Saved ${saved.length} of ${results.length} ${itemLabelPlural}`
        : `Couldn't save ${failed.length} ${failed.length === 1 ? itemLabel : itemLabelPlural}`,
      description: reasons.join(' · '),
      options: { variant: saved.length ? 'warning' : 'error', anchorOrigin: SNACKBAR_ANCHOR },
    });
  };

  const runSave = async () => {
    setSaving(true);
    const results: { label: string; outcome: RolesEditorSaveOutcome }[] = [];
    try {
      // Sequential and never aborting: each editor fires up to three requests
      // against the same company, and a rejection on one row must not stop the
      // next. Registration order is DOM order, so the report matches the screen.
      for (const entry of dirty) {
        const outcome = await entry.actions.current.save({ silent: true });
        if (outcome.status !== 'skipped') results.push({ label: entry.summary.label, outcome });
      }
      report(results);
    } finally {
      setSaving(false);
    }
  };

  if (!dirty.length) return null;

  const handleSaveClick = () => {
    if (dirty.some(entry => entry.summary.needsExceptionsConfirm)) {
      setConfirmSave(true);
      return;
    }
    runSave();
  };

  const runDiscard = () => dirty.forEach(entry => entry.actions.current.discard());

  const handleDiscardClick = () => {
    // Throwing away several people's edits on one click has no undo.
    if (dirty.length > 1) {
      setConfirmDiscard(true);
      return;
    }
    runDiscard();
  };

  return (
    <>
      <div className={classes.pageSaveBar}>
        <div className={classes.pageSaveBarText}>
          {summaryText}
          {!!blocked.length && (
            <div className={classes.pageSaveBarError}>
              {/* Name the person: one blocked row disables Save for the page, and
                  an unattributed reason looks like the button is simply broken. */}
              {`${blocked[0].summary.label || 'One row'}: ${blocked[0].summary.invalidReason}`}
              {blocked.length > 1 && ` (+${blocked.length - 1} more)`}
            </div>
          )}
        </div>

        <div className={classes.pageSaveBarActions}>
          <AppButton size="small" disabled={saving} onClick={handleDiscardClick}>
            Discard
          </AppButton>
          <AppButton
            size="small"
            variant="contained"
            color="primary"
            loading={saving}
            loaderVariant="paper"
            disabled={!!blocked.length}
            onClick={handleSaveClick}>
            {dirty.length > 1 ? `Save ${dirty.length} changes` : 'Save changes'}
          </AppButton>
        </div>
      </div>

      <ConfirmDialog
        open={confirmSave}
        title="Clear per-location exceptions?"
        subtitle={`"Apply to all" replaces every per-resource exception in the changed modules with the permissions above. This can’t be undone.`}
        confirmText="Apply to all"
        cancelText="Cancel"
        loading={saving}
        handleClose={() => setConfirmSave(false)}
        handleConfirm={() => {
          setConfirmSave(false);
          runSave();
        }}
      />

      <ConfirmDialog
        open={confirmDiscard}
        title={`Discard changes for ${dirty.length} ${itemLabelPlural}?`}
        subtitle="Everything edited on this page since the last save will be lost."
        confirmText="Discard"
        cancelText="Keep editing"
        handleClose={() => setConfirmDiscard(false)}
        handleConfirm={() => {
          setConfirmDiscard(false);
          runDiscard();
        }}
      />

      {/* Keeps the bar from sitting on the last row when the page ends here. */}
      <Box height={4} />
    </>
  );
};
